import { ILocationMatrixPair, ILocationValue, IPosition } from "components/Template/types";

const region = process.env.REACT_APP_AWS_LOCATION_REGION;
const apiKey = process.env.REACT_APP_AWS_LOCATION_API_KEY;

export const hasAwsLocationConfiguration = Boolean(region && apiKey);

export const mapStyleUrl = hasAwsLocationConfiguration
  ? `https://maps.geo.${region}.amazonaws.com/v2/styles/Standard/descriptor?key=${encodeURIComponent(apiKey as string)}`
  : "";

const request = async (service: "places" | "routes", path: string, body: object, signal?: AbortSignal) => {
  if (!region || !apiKey) {
    throw new Error("Amazon Location is not configured. Provide its API key and region when building the frontend.");
  }
  const response = await fetch(
    `https://${service}.geo.${region}.amazonaws.com${path}?key=${encodeURIComponent(apiKey)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    }
  );
  if (!response.ok) {
    let detail = "";
    try {
      const error = await response.json();
      detail = error?.message || error?.Message || "";
    } catch (_error) {
      // The response status below remains useful when AWS does not return JSON.
    }
    throw new Error(detail || `Amazon Location request failed (${response.status} ${response.statusText}).`);
  }
  return response.json();
};

export const suggestLocations = async (
  query: string,
  biasPosition?: IPosition,
  signal?: AbortSignal
): Promise<Array<string>> => {
  const data = await request(
    "places",
    "/v2/suggest",
    {
      QueryText: query,
      MaxResults: 6,
      MaxQueryRefinements: 2,
      IntendedUse: "SingleUse",
      TravelMode: "Car",
      ...(biasPosition ? { BiasPosition: biasPosition } : {}),
    },
    signal
  );
  return (data.ResultItems || [])
    .map((item: any) => item.Title || item.Place?.Title || "")
    .filter(Boolean);
};

export const searchLocation = async (
  query: string,
  biasPosition?: IPosition,
  signal?: AbortSignal
): Promise<ILocationValue> => {
  const data = await request(
    "places",
    "/v2/search-text",
    {
      QueryText: query,
      MaxResults: 1,
      IntendedUse: "Storage",
      ...(biasPosition ? { BiasPosition: biasPosition } : {}),
    },
    signal
  );
  const item = data.ResultItems?.[0];
  const position = item?.Position || item?.Place?.Position;
  if (!item || !Array.isArray(position) || position.length !== 2) {
    throw new Error(`No location found for “${query}”.`);
  }
  return {
    label: item.Title || item.Place?.Title || item.Address?.Label || query,
    position: [Number(position[0]), Number(position[1])],
    ...(item.PlaceId || item.Place?.PlaceId ? { placeId: item.PlaceId || item.Place.PlaceId } : {}),
  };
};

const getLegSummary = (leg: any) => {
  const overview =
    leg.VehicleLegDetails?.Summary?.Overview ||
    leg.PedestrianLegDetails?.Summary?.Overview ||
    leg.FerryLegDetails?.Summary?.Overview ||
    leg.TransitLegDetails?.Summary?.Overview ||
    {};
  return {
    distanceMeters: Number(overview.Distance || 0),
    durationSeconds: Number(overview.Duration || 0),
  };
};

export const calculateDrivingPair = async (
  from: ILocationValue,
  to: ILocationValue,
  fromIndex: number,
  toIndex: number,
  signal?: AbortSignal
): Promise<ILocationMatrixPair> => {
  if (!from.position || !to.position) {
    throw new Error("Both locations must be resolved before calculating a route.");
  }
  const data = await request(
    "routes",
    "/v2/routes",
    {
      Origin: from.position,
      Destination: to.position,
      TravelMode: "Car",
      OptimizeRoutingFor: "FastestRoute",
      LegGeometryFormat: "Simple",
      LegAdditionalFeatures: ["Summary", "TypicalDuration"],
    },
    signal
  );
  const route = data.Routes?.[0];
  if (!route?.Legs?.length) {
    throw new Error(`No drivable route was found between “${from.label}” and “${to.label}”.`);
  }
  const legSummaries = route.Legs.map(getLegSummary);
  const geometry = route.Legs.flatMap((leg: any, index: number) => {
    const points = (leg.Geometry?.LineString || []) as Array<IPosition>;
    return index ? points.slice(1) : points;
  });
  return {
    fromIndex,
    toIndex,
    distanceMeters: Number(route.Summary?.Distance || legSummaries.reduce((sum: number, leg: any) => sum + leg.distanceMeters, 0)),
    durationSeconds: Number(route.Summary?.Duration || legSummaries.reduce((sum: number, leg: any) => sum + leg.durationSeconds, 0)),
    geometry,
  };
};
